/**
 * Helper functions for creating VishMaker payloads
 * These bridge the gap between legacy code and the new gateway_ws system
 */

import type { VishMakerPayload, Channel, Actor, Status, BodyKind, Origin, MessageType } from './gateway_ws/outbound';

/**
 * Create a VishMaker payload (for use with WebSocketRouter.sendRequest or direct sending)
 * This replaces the deprecated commManager.createVishmakerPayload
 */
export function createVishmakerPayload(
    actor: Actor,
    payload: Record<string, any>,
    channel: Channel,
    bodyKind: BodyKind,
    messageType: MessageType = 'request',
    status: Status = 'connected',
    options?: {
        userId?: string;
        projectId?: string;
        page?: string;
        threadId?: string;
        reply?: {
            parentMessageId: string;
            parentActor: Actor;
            parentts: string;
        };
    }
): VishMakerPayload {
    const messageId = crypto.randomUUID();
    const timestamp = new Date().toISOString();
    const threadId = options?.threadId || `thread_${channel}_${Date.now()}`;

    const origin: Origin = {
        threadId,
        originMessageId: messageId,
        originActor: actor,
        origints: timestamp,
        ...(options?.userId && { userId: options.userId }),
        ...(options?.projectId && { projectId: options.projectId }),
        ...(options?.page && { page: options.page })
    };

    // Build the payload structure matching backend exactly
    const vishmakerPayload: VishMakerPayload = {
        v: '1.0',
        id: messageId,
        ts: timestamp,
        actor,
        type: messageType,
        channel,
        status,
        origin,
        body: {
            kind: bodyKind,
            payload: payload
        }
    };

    // Add reply field if provided
    if (options?.reply) {
        vishmakerPayload.reply = {
            parentMessageId: options.reply.parentMessageId,
            parentActor: options.reply.parentActor,
            parentts: options.reply.parentts
        };
    }

    return vishmakerPayload;
}

/**
 * Parse a response from VishCoder (replaces deprecated commManager.parseResponse)
 * This handles both the new VishMakerPayload format and legacy formats
 */
export function parseResponse(data: any): {
    type: string;
    actor: string;
    status: string;
    body: any;
    messageId?: string;
} | null {
    // If it's already a VishMakerPayload, extract the relevant fields
    if (data && typeof data === 'object') {
        // Check for new format
        if (data.v && data.channel && data.body) {
            return {
                type: data.body.kind || data.type || 'unknown',
                actor: data.actor || 'Unknown',
                status: data.status || 'unknown',
                body: data.body.payload || data.body,
                messageId: data.id
            };
        }

        // Check for legacy format with type field
        if (data.type) {
            return {
                type: data.type,
                actor: data.actor || data.source || 'Unknown',
                status: data.status || 'unknown',
                body: data.body || data.messages || data,
                messageId: data.messageId || data.id
            };
        }

        // Check for legacy format with schema_version
        if (data.schema_version && data.message) {
            return {
                type: data.message.type || 'unknown',
                actor: data.message.actor || 'Unknown',
                status: 'completed',
                body: { message: data.message },
                messageId: data.message.message_id
            };
        }
    }

    return null;
}

/**
 * Get WebSocket URL based on environment
 */
export function getWebSocketUrl(): string {
    const environment = import.meta.env.VITE_ENVIRONMENT || 'prod';
    const baseUrl = environment === 'prod' 
        ? 'wss://vishmaker.com' 
        : `wss://${environment}.vishmaker.com`;
    
    return `${baseUrl}/ws`;
}
